/**
 * AreasPage — manage areas and their postcodes (Settings → Areas).
 *
 * An area belongs to an optional region and holds a list of postcodes. Teams
 * assigned to an area cover its postcodes for availability.
 */

import { PageHeader } from '@/shared/components';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@/shared/ui';
import type { Area } from '@skewb-works/contracts';
import {
  ChevronLeft,
  ChevronRight,
  Home,
  MapPin,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  useAreas,
  useCreateArea,
  useDeleteArea,
  useRegions,
  useUpdateArea,
} from '../hooks/useLocations';

const NO_REGION = '__none__';

// Split a free-text postcode entry (newlines / commas) into a clean list.
function parsePostcodes(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,]/)
        .map((p) => p.trim().toUpperCase())
        .filter((p) => p.length > 0),
    ),
  );
}

const PAGE_SIZE = 25;

export function AreasPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  // Debounce the search term so each keystroke doesn't fire a request.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useAreas({
    search: debouncedSearch || undefined,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });
  const areas = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const { data: regions = [] } = useRegions();
  const createMutation = useCreateArea();
  const [editingId, setEditingId] = useState<number | null>(null);
  const updateMutation = useUpdateArea(editingId ?? 0);
  const deleteMutation = useDeleteArea();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [contractor, setContractor] = useState('');
  const [regionId, setRegionId] = useState<string>(NO_REGION);
  const [postcodesText, setPostcodesText] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  function openCreate() {
    setEditingId(null);
    setName('');
    setContractor('');
    setRegionId(NO_REGION);
    setPostcodesText('');
    setDialogOpen(true);
  }

  function openEdit(areaItem: Area) {
    setEditingId(areaItem.id);
    setName(areaItem.name ?? '');
    setContractor(areaItem.contractor ?? '');
    setRegionId(areaItem.region_id != null ? String(areaItem.region_id) : NO_REGION);
    setPostcodesText(areaItem.postcodes.join('\n'));
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!name.trim()) return;
    const body = {
      name,
      contractor: contractor || null,
      region_id: regionId === NO_REGION ? null : Number(regionId),
      postcodes: parsePostcodes(postcodesText),
    };
    try {
      if (editingId) {
        await updateMutation.mutateAsync(body);
      } else {
        await createMutation.mutateAsync(body);
      }
      showSuccessToast(editingId ? 'Area updated.' : 'Area created.');
      setDialogOpen(false);
    } catch (error: any) {
      showErrorToast('Save failed', error?.message || 'An unexpected error occurred.');
    }
  }

  async function handleDelete() {
    if (deleteId === null) return;
    try {
      await deleteMutation.mutateAsync(deleteId);
      showSuccessToast('Area deleted.');
    } catch (error: any) {
      showErrorToast('Delete failed', error?.message || 'An unexpected error occurred.');
    } finally {
      setDeleteId(null);
    }
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Areas"
        subtitle="Manage areas and the postcodes they cover"
        icon={<MapPin className="w-8 h-8" style={{ color: '#E84A2B' }} />}
        breadcrumbs={[
          { label: <Home className="w-4 h-4" />, href: '/', current: false },
          { label: 'Settings', href: '/settings', current: false },
          { label: 'Areas', current: true },
        ]}
        actions={
          <Button
            onClick={openCreate}
            style={{ backgroundColor: '#E84A2B' }}
            className="text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Add Area
          </Button>
        }
      />

      <div className="px-6 pb-6 flex-1">
        <div className="mb-4 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search areas, regions, postcodes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Region</TableHead>
                <TableHead>Postcodes</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : areas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    {debouncedSearch ? 'No areas match your search.' : 'No areas yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                areas.map((areaItem: Area) => (
                  <TableRow key={areaItem.id} className="border-border hover:bg-muted/50">
                    <TableCell className="font-medium text-foreground">{areaItem.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {areaItem.region_name ?? '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-md">
                        {areaItem.postcodes.slice(0, 8).map((pc: string) => (
                          <Badge key={pc} variant="secondary" className="text-[10px]">
                            {pc}
                          </Badge>
                        ))}
                        {areaItem.postcodes.length > 8 && (
                          <span className="text-xs text-muted-foreground">
                            +{areaItem.postcodes.length - 8}
                          </span>
                        )}
                        {areaItem.postcodes.length === 0 && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(areaItem)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteId(areaItem.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Area' : 'Add Area'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="area-name">Name *</Label>
              <Input
                id="area-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Leeds"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="area-region">Region</Label>
              <Select value={regionId} onValueChange={setRegionId}>
                <SelectTrigger id="area-region">
                  <SelectValue placeholder="No region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_REGION}>No region</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={String(r.id)}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="area-contractor">Contractor</Label>
              <Input
                id="area-contractor"
                value={contractor}
                onChange={(e) => setContractor(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="area-postcodes">Postcodes</Label>
              <Textarea
                id="area-postcodes"
                value={postcodesText}
                onChange={(e) => setPostcodesText(e.target.value)}
                rows={5}
                placeholder="One per line, or comma-separated (e.g. LS1, LS2)"
              />
              <p className="text-[11px] text-muted-foreground">
                {parsePostcodes(postcodesText).length} postcode(s). Used to match jobs to teams by
                postcode.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || isSaving}
              style={{ backgroundColor: '#E84A2B' }}
              className="text-white"
            >
              {isSaving ? 'Saving...' : editingId ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Area</AlertDialogTitle>
            <AlertDialogDescription>
              This area and its postcodes will be deleted. Teams assigned to it lose this coverage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
